import { useEffect, useMemo, useRef, useState } from 'react';
import { ChangeSet, type Change } from 'prosemirror-changeset';
import { Schema, Node as PmNode, DOMSerializer } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Transform } from '@tiptap/pm/transform';
import { editorExtensions } from './extensions';
import { getSchema } from '@tiptap/core';

export interface Hunk {
  /** Position in the changeset, used as a render key only. NOT a stable
   *  dismiss key — the index changes between renders as hunks resolve. */
  index: number;
  /** Range in the basedOn doc that this hunk replaces (toA == fromA for pure insertions). */
  fromA: number;
  toA: number;
  /** Range in the suggested doc that supplies the replacement (toB == fromB for pure deletions). */
  fromB: number;
  toB: number;
  /** What kind of hunk for UI/affordance purposes. */
  kind: 'insertion' | 'deletion' | 'replacement';
  /** Stable content key used to remember user dismissals across renders. */
  dismissKey: string;
  removedImages?: PmNode[];
}

interface Props {
  basedOnDoc: unknown;
  suggestedDoc: unknown;
  /**
   * Called when the admin clicks the ✓ on a hunk. The parent applies the
   * hunk to the master draft (typically by dispatching a transaction on the
   * master editor) and returns whether it succeeded.
   *
   * After accept, we don't track anything client-side — the caller is
   * expected to update the basedOn input (e.g. by passing the live master)
   * so the hunk vanishes naturally on the next render.
   *
   * If absent, hunks render without per-hunk actions (read-only diff).
   */
  onAcceptHunk?: (hunk: Hunk) => boolean | Promise<boolean>;
  /** When set, dismissals reset only when this changes, not whenever
   *  `suggestedDoc` does (2A rebases the suggestion on every master edit). */
  resetKey?: string;
}

const diffPluginKey = new PluginKey('hsp-diff');

/**
 * Read-only renderer that shows the suggested doc with insertions highlighted
 * green and deletions surfaced inline as struck-through red widgets.
 *
 * When `onAcceptHunk` is provided, each hunk gains a small floating ✓/✗
 * button group: ✓ asks the parent to apply that hunk to the master draft,
 * ✗ marks the hunk as rejected so it disappears from the view.
 *
 * Dismissals (✗) are keyed by content (text + kind), not index, so they
 * survive remounts and re-renders triggered by polling. Acceptances (✓)
 * don't need explicit tracking — when the parent passes the live master as
 * basedOn, an applied hunk simply doesn't appear in the next diff.
 */
export default function DiffView({ basedOnDoc, suggestedDoc, onAcceptHunk, resetKey }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  const schema = useMemo(() => getSchema(editorExtensions), []);

  const { mergedDoc, changes, originalText, hunks, failed } = useMemo(() => {
    return computeDiff(schema, basedOnDoc, suggestedDoc);
  }, [schema, basedOnDoc, suggestedDoc]);

  // Content-keyed rejection set. Reset only when the suggestion content
  // changes — basedOn changing (because the live master shifted) is
  // expected on every accept and must NOT reset the user's rejections.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const resetOn = resetKey ?? suggestedDoc;
  useEffect(() => {
    setDismissed(new Set());
  }, [resetOn]);

  const dismissHunk = (hunk: Hunk) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(hunk.dismissKey);
      return next;
    });
  };

  const handleAccept = async (hunk: Hunk) => {
    if (!onAcceptHunk) return;
    await onAcceptHunk(hunk);
    // No bookkeeping needed — the parent will pass an updated basedOn
    // (live master) on the next render and the hunk drops out of the diff.
  };

  // The decoration plugin reads dismissed/handlers via this ref so we don't
  // have to rebuild the EditorView on every dismiss — only the decorations
  // re-render via a no-op transaction.
  const liveRef = useRef({
    dismissed,
    handleAccept,
    dismissHunk,
    hunks,
    changes,
    originalText,
    onAcceptHunk,
  });
  liveRef.current = {
    dismissed,
    handleAccept,
    dismissHunk,
    hunks,
    changes,
    originalText,
    onAcceptHunk,
  };

  // Build (or rebuild) the read-only PM view when the input docs change.
  useEffect(() => {
    if (!containerRef.current || failed) return;

    const state = EditorState.create({
      doc: mergedDoc,
      schema,
      plugins: [
        new Plugin({
          key: diffPluginKey,
          props: {
            decorations(state) {
              const live = liveRef.current;
              return buildDecorations(
                state.doc,
                live.changes,
                live.originalText,
                live.hunks,
                live.dismissed,
                live.onAcceptHunk ? live.handleAccept : null,
                live.dismissHunk,
              );
            },
            editable: () => false,
          },
        }),
      ],
    });

    const view = new EditorView(containerRef.current, {
      state,
      editable: () => false,
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [mergedDoc, schema, failed]);

  // When dismissed changes, dispatch a meta-tagged tx so PM's
  // decorations() re-runs and dismissed hunks drop out of the view.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch(view.state.tr.setMeta('hsp-diff-redecorate', true));
  }, [dismissed]);

  if (failed) {
    return (
      <p className="text-accent text-[13px] leading-[1.55]">
        These documents couldn't be compared because one of them isn't in a format the editor
        recognises. Open the suggestion on its own to read it.
      </p>
    );
  }

  return <div ref={containerRef} className="hsp-editor hsp-diff" />;
}

interface DiffResult {
  mergedDoc: PmNode;
  changes: readonly Change[];
  /** Map of change.id → original (deleted) text, looked up at decoration time. */
  originalText: Map<number, string>;
  hunks: Hunk[];
  /** True when a document couldn't be read into the schema at all. */
  failed?: boolean;
}

export function computeDiff(
  schema: Schema,
  basedOnDoc: unknown,
  suggestedDoc: unknown,
): DiffResult {
  let fromDoc: PmNode;
  let toDoc: PmNode;
  try {
    fromDoc = schema.nodeFromJSON(basedOnDoc);
    toDoc = schema.nodeFromJSON(suggestedDoc);
  } catch {
    // nodeFromJSON throws on anything the schema doesn't recognise, and this
    // runs inside a render-phase useMemo — an uncaught throw here takes the
    // whole editor page down. Report the failure instead.
    const empty = schema.topNodeType.createAndFill();
    return {
      mergedDoc: empty ?? schema.topNodeType.create(),
      changes: [],
      originalText: new Map(),
      hunks: [],
      failed: true,
    };
  }

  // Build a step that transforms fromDoc → toDoc by replacing all content.
  // ChangeSet uses the step maps to derive granular text-level changes.
  const tr = new Transform(fromDoc);
  try {
    tr.replaceWith(0, fromDoc.content.size, toDoc.content);
  } catch {
    return { mergedDoc: toDoc, changes: [], originalText: new Map(), hunks: [] };
  }

  let cs = ChangeSet.create(fromDoc, undefined, {
    encodeCharacter: (char) => char,
    encodeNodeStart: (node) => node.type.name === 'refImage'
      ? `refImage:${JSON.stringify(node.attrs)}` : node.type.name,
    encodeNodeEnd: (node) => `/${node.type.name}`,
    compareTokens: (a, b) => a === b,
  });
  cs = cs.addSteps(tr.doc, tr.mapping.maps, 1);

  const originalText = new Map<number, string>();
  const hunks: Hunk[] = [];
  for (let i = 0; i < cs.changes.length; i++) {
    const change = cs.changes[i]!;
    const hasDelete = change.toA > change.fromA;
    const hasInsert = change.toB > change.fromB;
    let delText = '';
    if (hasDelete) {
      try {
        delText = fromDoc.textBetween(change.fromA, change.toA, '\n', ' ');
        if (delText.trim().length > 0) originalText.set(i, delText);
      } catch {
        // ignore
      }
    }
    let insText = '';
    if (hasInsert) {
      try {
        insText = tr.doc.textBetween(change.fromB, change.toB, '\n', ' ');
      } catch {
        // ignore
      }
    }
    const kind: Hunk['kind'] = hasDelete && hasInsert
      ? 'replacement'
      : hasInsert
      ? 'insertion'
      : 'deletion';
    const imagesIn = (doc: PmNode, from: number, to: number): PmNode[] => {
      const images: PmNode[] = [];
      if (to > from) doc.nodesBetween(from, to, (node) => {
        if (node.type.name === 'refImage') { images.push(node); return false; }
        return true;
      });
      return images;
    };
    const removedImages = imagesIn(fromDoc, change.fromA, change.toA);
    const insertedImages = imagesIn(tr.doc, change.fromB, change.toB);
    hunks.push({
      index: i,
      fromA: change.fromA,
      toA: change.toA,
      fromB: change.fromB,
      toB: change.toB,
      kind,
      // Content-based key — survives remounts/poll churn so a rejected
      // hunk doesn't pop back into the diff. Identical hunks (same kind,
      // same text) collapse to one key, which is fine for our use case.
      dismissKey: `${kind}|${delText}|${insText}|${removedImages.map((n) => n.attrs.imageId).join(',')}|${insertedImages.map((n) => n.attrs.imageId).join(',')}`,
      removedImages,
    });
  }

  return { mergedDoc: tr.doc, changes: cs.changes, originalText, hunks };
}

function buildDecorations(
  doc: PmNode,
  changes: readonly Change[],
  originalText: Map<number, string>,
  hunks: Hunk[],
  dismissed: Set<string>,
  onAccept: ((hunk: Hunk) => void | Promise<void>) | null,
  onDismiss: (hunk: Hunk) => void,
): DecorationSet {
  const decos: Decoration[] = [];
  const docSize = doc.content.size;

  changes.forEach((change, i) => {
    const hunk = hunks[i];
    if (!hunk) return;
    if (dismissed.has(hunk.dismissKey)) return;

    if (change.toB > change.fromB) {
      const from = Math.max(0, Math.min(change.fromB, docSize));
      const to = Math.max(from, Math.min(change.toB, docSize));
      if (to > from) {
        decos.push(Decoration.inline(from, to, { class: 'diff-inserted' }));
        // Reference images (2B) are atoms an inline mark can't reach.
        doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name !== 'refImage') return true;
          decos.push(Decoration.node(pos, pos + node.nodeSize, { class: 'diff-image-added' }));
          return false;
        });
      }
    }

    for (const image of hunk.removedImages ?? []) {
      const figure = DOMSerializer.fromSchema(doc.type.schema).serializeNode(image) as HTMLElement;
      figure.classList.add('diff-image-removed');
      figure.setAttribute('aria-label', 'Image removed');
      decos.push(Decoration.widget(Math.max(0, Math.min(change.fromB, docSize)), figure, {
        side: -1, ignoreSelection: true, key: `del-image-${i}-${image.attrs.imageId}`,
      }));
    }

    const removedText = originalText.get(i);
    if (removedText) {
      const widgetPos = Math.max(0, Math.min(change.fromB, docSize));
      const widget = document.createElement('span');
      widget.className = 'diff-removed';
      widget.textContent = removedText;
      decos.push(
        Decoration.widget(widgetPos, widget, {
          side: -1,
          ignoreSelection: true,
          key: `del-${i}`,
        }),
      );
    }

    if (onAccept) {
      const buttonsPos = Math.max(0, Math.min(change.fromB, docSize));
      const buttons = makeHunkButtons(hunk, onAccept, onDismiss);
      decos.push(
        Decoration.widget(buttonsPos, buttons, {
          // side -2 so the button group sits to the left of the deletion widget.
          side: -2,
          ignoreSelection: true,
          key: `act-${i}`,
        }),
      );
    }
  });

  return DecorationSet.create(doc, decos);
}

function makeHunkButtons(
  hunk: Hunk,
  onAccept: (hunk: Hunk) => void | Promise<void>,
  onDismiss: (hunk: Hunk) => void,
): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'diff-hunk-actions';
  wrap.contentEditable = 'false';
  wrap.addEventListener('mousedown', (e) => e.preventDefault());

  const accept = document.createElement('button');
  accept.type = 'button';
  accept.className = 'diff-hunk-action diff-hunk-accept';
  accept.textContent = '✓';
  accept.title = 'Accept this change into the master draft';
  accept.setAttribute('aria-label', 'Accept this change');
  accept.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    void onAccept(hunk);
  });

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'diff-hunk-action diff-hunk-dismiss';
  dismiss.textContent = '✗';
  dismiss.title = 'Dismiss this change from the diff view';
  dismiss.setAttribute('aria-label', 'Keep original');
  dismiss.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDismiss(hunk);
  });

  wrap.appendChild(accept);
  wrap.appendChild(dismiss);
  return wrap;
}
